import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Handshake, Plus, Search, X, Loader2, Pencil, Trash2, Eye, UserPlus, FileSpreadsheet, Download } from "lucide-react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { formatPYG } from "../utils/format";
import { toast } from "sonner";
import { normalizar } from "../components/SelectBuscable";
import { exportarReporte, fechaReporte } from "../utils/reportes";

/* Fix EE (2026-09-24): Tesorería → Tercerizados (CEN, 23/09): "registrar a los tercerizados
   con razón social, RUC, teléfono, dirección, mostrar contratos activos a su razón social,
   obras asignadas y un historial de obras asignadas a ellos por mes/año… informes históricos
   de montos contratados en las distintas obras y desde allí ir a ver los presupuestos y
   centro de costos… Desde allí se alimenta a los CONTRATOS con tercerizados de OBRA y a
   PAGOS SERV. TERCERIZADOS". */
const opts = { withCredentials: true };
const VACIO = { razon_social: "", ruc: "", telefono: "", direccion: "" };
const mesTexto = (p) => {
  const [a, m] = String(p || "").split("-");
  const n = ["", "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
  return m ? `${n[Number(m)] || m} ${a}` : p;
};

function Ventana({ titulo, ancho = "max-w-lg", onClose, children, testId }) {
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid={testId}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full ${ancho} max-h-[92vh] flex flex-col`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-base font-bold text-gray-900">{titulo}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid={`${testId}-cerrar`}><X size={17} className="text-gray-500" /></button>
        </div>
        <div className="overflow-y-auto p-6">{children}</div>
      </div>
    </div>,
    document.body
  );
}

function FormTercerizado({ inicial, onClose, onSaved }) {
  const [form, setForm] = useState({ ...VACIO, ...inicial });
  const [saving, setSaving] = useState(false);
  const guardar = async (e) => {
    e.preventDefault();
    if (!form.razon_social.trim()) { toast.error("La razón social es obligatoria"); return; }
    setSaving(true);
    try {
      if (inicial?.id) await axios.put(`${API}/tercerizados/registro/${inicial.id}`, form, opts);
      else await axios.post(`${API}/tercerizados/registro`, form, opts);
      toast.success(inicial?.id ? "Tercerizado actualizado" : "Tercerizado registrado");
      onSaved();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };
  const campo = (k, l, ph) => (
    <div>
      <label className="block text-xs font-semibold text-gray-700 mb-1.5">{l}</label>
      <input value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} placeholder={ph}
        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" data-testid={`terc-form-${k}`} />
    </div>
  );
  return (
    <Ventana titulo={inicial?.id ? "Editar tercerizado" : "Nuevo tercerizado"} onClose={onClose} testId="terc-form">
      <form onSubmit={guardar} className="space-y-3">
        {campo("razon_social", "Razón social *", "Nombre o razón social")}
        {campo("ruc", "RUC", "Ej: 80012345-6")}
        {campo("telefono", "Teléfono", "Ej: 0981 123 456")}
        {campo("direccion", "Dirección", "Calle, número, ciudad")}
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
          <button type="submit" disabled={saving} className="flex-1 bg-[#10B981] hover:bg-[#059669] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="terc-form-guardar">
            {saving && <Loader2 size={15} className="animate-spin" />} Guardar
          </button>
        </div>
      </form>
    </Ventana>
  );
}

function DetalleTercerizado({ tercerizado, onClose }) {
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [data, setData] = useState(null);
  const [exportando, setExportando] = useState("");
  const navigate = useNavigate();
  useEffect(() => {
    const qs = new URLSearchParams({ ...(desde ? { desde } : {}), ...(hasta ? { hasta } : {}) }).toString();
    axios.get(`${API}/tercerizados/registro/${tercerizado.id}/detalle${qs ? `?${qs}` : ""}`, opts)
      .then(({ data }) => setData(data))
      .catch(err => toast.error(err.response?.data?.detail || "No se pudo cargar el detalle"));
  }, [tercerizado.id, desde, hasta]);
  const ir = (obraId, tab) => { onClose(); navigate(`/obra/${obraId}?tab=${tab}`); };
  const t = data?.totales || {};
  const exportar = (tipo) => exportarReporte(tipo, {
    titulo: `Tercerizado ${tercerizado.razon_social} — contratos`,
    filtros: [tercerizado.ruc ? `RUC ${tercerizado.ruc}` : "", desde ? `Desde ${fechaReporte(desde)}` : "", hasta ? `Hasta ${fechaReporte(hasta)}` : ""].filter(Boolean).join(" · "),
    columnas: [{ l: "Fecha" }, { l: "Obra" }, { l: "Contrato" }, { l: "Tareas", num: true }, { l: "Monto contratado", num: true },
      { l: "Certificado", num: true }, { l: "Pagado", num: true }, { l: "Saldo", num: true }, { l: "Estado" }],
    filas: (data?.contratos || []).map(c => [fechaReporte(c.fecha), `${c.obra_codigo} ${c.obra}`, c.adenda_numero ? `Adenda ${c.adenda_numero}` : "Contrato",
      c.tareas, c.monto_cerrado, c.certificado, c.pagado, c.saldo, c.activo ? "Activo" : "Terminado"]),
    totales: ["Totales", "", "", "", t.monto_contratado, t.certificado, t.pagado, t.saldo, ""],
    archivo: `Tercerizado_${tercerizado.razon_social}`.replace(/[^A-Za-z0-9_]+/g, "_"),
  }, setExportando);
  return (
    <Ventana titulo={tercerizado.razon_social} ancho="max-w-6xl" onClose={onClose} testId="terc-detalle">
      <div className="space-y-5">
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-600">
          <span>RUC: <strong>{tercerizado.ruc || "—"}</strong></span>
          <span>Teléfono: <strong>{tercerizado.telefono || "—"}</strong></span>
          <span>Dirección: <strong>{tercerizado.direccion || "—"}</strong></span>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div><label className="block text-[11px] font-semibold text-gray-500 mb-1">Desde</label>
            <input type="date" value={desde} onChange={e => setDesde(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs" data-testid="terc-desde" /></div>
          <div><label className="block text-[11px] font-semibold text-gray-500 mb-1">Hasta</label>
            <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs" data-testid="terc-hasta" /></div>
          <div className="flex gap-2 ml-auto">
            <button onClick={() => exportar("pdf")} disabled={!!exportando || !data} className="flex items-center gap-1 text-xs font-semibold border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50" data-testid="terc-detalle-pdf"><Download size={13} /> PDF</button>
            <button onClick={() => exportar("excel")} disabled={!!exportando || !data} className="flex items-center gap-1 text-xs font-semibold border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50" data-testid="terc-detalle-excel"><FileSpreadsheet size={13} /> Excel</button>
            <button onClick={() => exportar("ver")} disabled={!!exportando || !data} className="flex items-center gap-1 text-xs font-semibold border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50" data-testid="terc-detalle-preview"><Eye size={13} /> Vista previa</button>
          </div>
        </div>
        {!data ? <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div> : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="terc-kpis">
              {[["Contratos activos", t.contratos_activos], ["Obras", t.obras], ["Monto contratado", formatPYG(t.monto_contratado)],
                ["Pagado", formatPYG(t.pagado)], ["Saldo", formatPYG(t.saldo)]].map(([l, v]) => (
                <div key={l} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase">{l}</p>
                  <p className="text-sm font-bold text-gray-800 mt-0.5">{v ?? 0}</p>
                </div>
              ))}
            </div>

            <div>
              <h3 className="text-xs font-bold text-gray-700 uppercase mb-2">Contratos</h3>
              <div className="overflow-x-auto rounded-xl border border-gray-100">
                <table className="w-full text-xs" data-testid="terc-contratos">
                  <thead className="bg-gray-50 text-[10px] text-gray-500 uppercase"><tr>
                    <th className="text-left px-3 py-2">Fecha</th><th className="text-left px-3 py-2">Obra</th><th className="text-left px-3 py-2">Contrato</th>
                    <th className="text-right px-3 py-2">Monto</th><th className="text-right px-3 py-2">Certificado</th><th className="text-right px-3 py-2">Pagado</th>
                    <th className="text-right px-3 py-2">Saldo</th><th className="text-left px-3 py-2">Estado</th><th className="px-3 py-2" />
                  </tr></thead>
                  <tbody>
                    {data.contratos.length === 0 && <tr><td colSpan={9} className="py-6 text-center text-gray-400">Sin contratos en ese período</td></tr>}
                    {data.contratos.map(c => (
                      <tr key={c.id} className="border-t border-gray-50" data-testid={`terc-contrato-${c.id}`}>
                        <td className="px-3 py-2">{fechaReporte(c.fecha)}</td>
                        <td className="px-3 py-2"><span className="font-semibold">{c.obra}</span> <span className="text-gray-400">{c.obra_codigo}</span></td>
                        <td className="px-3 py-2">{c.adenda_numero ? `Adenda ${c.adenda_numero}` : "Contrato"} · {c.tareas} tarea(s)</td>
                        <td className="px-3 py-2 text-right">{formatPYG(c.monto_cerrado)}</td>
                        <td className="px-3 py-2 text-right">{formatPYG(c.certificado)}</td>
                        <td className="px-3 py-2 text-right text-emerald-700">{formatPYG(c.pagado)}</td>
                        <td className="px-3 py-2 text-right font-bold">{formatPYG(c.saldo)}</td>
                        <td className="px-3 py-2">{c.activo ? <span className="text-emerald-700 font-semibold">Activo</span> : <span className="text-gray-400">Terminado</span>}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <button onClick={() => ir(c.obra_id, "pagos")} className="text-[#2E6DA4] font-semibold hover:underline" data-testid={`terc-ir-pagos-${c.id}`}>Pagos</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h3 className="text-xs font-bold text-gray-700 uppercase mb-2">Obras asignadas</h3>
              <div className="overflow-x-auto rounded-xl border border-gray-100">
                <table className="w-full text-xs" data-testid="terc-obras">
                  <thead className="bg-gray-50 text-[10px] text-gray-500 uppercase"><tr>
                    <th className="text-left px-3 py-2">Obra</th><th className="text-left px-3 py-2">Período</th><th className="text-right px-3 py-2">Contratos</th>
                    <th className="text-right px-3 py-2">Monto contratado</th><th className="text-right px-3 py-2">Pagado</th><th className="text-right px-3 py-2">Saldo</th><th className="px-3 py-2" />
                  </tr></thead>
                  <tbody>
                    {data.obras.map(o => (
                      <tr key={o.obra_id} className="border-t border-gray-50" data-testid={`terc-obra-${o.obra_id}`}>
                        <td className="px-3 py-2"><span className="font-semibold">{o.obra}</span> <span className="text-gray-400">{o.obra_codigo}</span></td>
                        <td className="px-3 py-2">{fechaReporte(o.desde)}{o.hasta !== o.desde ? ` al ${fechaReporte(o.hasta)}` : ""}</td>
                        <td className="px-3 py-2 text-right">{o.contratos}</td>
                        <td className="px-3 py-2 text-right">{formatPYG(o.monto_contratado)}</td>
                        <td className="px-3 py-2 text-right text-emerald-700">{formatPYG(o.pagado)}</td>
                        <td className="px-3 py-2 text-right font-bold">{formatPYG(o.saldo)}</td>
                        <td className="px-3 py-2 whitespace-nowrap space-x-2">
                          <button onClick={() => ir(o.obra_id, "presupuesto")} className="text-[#2E6DA4] font-semibold hover:underline" data-testid={`terc-ir-presupuesto-${o.obra_id}`}>Presupuesto</button>
                          <button onClick={() => ir(o.obra_id, "centro")} className="text-[#2E6DA4] font-semibold hover:underline" data-testid={`terc-ir-centro-${o.obra_id}`}>Centro de costos</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h3 className="text-xs font-bold text-gray-700 uppercase mb-2">Historial por mes</h3>
              <div className="overflow-x-auto rounded-xl border border-gray-100">
                <table className="w-full text-xs" data-testid="terc-historial">
                  <thead className="bg-gray-50 text-[10px] text-gray-500 uppercase"><tr>
                    <th className="text-left px-3 py-2">Mes</th><th className="text-left px-3 py-2">Obras</th><th className="text-right px-3 py-2">Contratos</th><th className="text-right px-3 py-2">Monto contratado</th>
                  </tr></thead>
                  <tbody>
                    {data.historial.map(h => (
                      <tr key={h.periodo} className="border-t border-gray-50">
                        <td className="px-3 py-2 font-semibold">{mesTexto(h.periodo)}</td>
                        <td className="px-3 py-2">{h.obras.join(", ")}</td>
                        <td className="px-3 py-2 text-right">{h.contratos}</td>
                        <td className="px-3 py-2 text-right">{formatPYG(h.monto_contratado)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </Ventana>
  );
}

export default function TesoreriaTercerizados() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [exportando, setExportando] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/tercerizados/registro`, opts)
      .then(({ data }) => setData(data))
      .catch(err => { toast.error(err.response?.data?.detail || "No se pudieron cargar los tercerizados"); setData({ tercerizados: [], sin_registrar: [] }); });
  }, []);
  useEffect(load, [load]);

  const filas = (data?.tercerizados || []).filter(t => !q.trim()
    || normalizar(`${t.razon_social} ${t.ruc} ${t.telefono} ${t.direccion}`).includes(normalizar(q.trim())));
  const suma = (k) => filas.reduce((s, t) => s + (Number(t[k]) || 0), 0);

  const borrar = async (t) => {
    if (!window.confirm(`¿Enviar a ${t.razon_social} a la papelera? Sus contratos no cambian.`)) return;
    try { await axios.delete(`${API}/tercerizados/registro/${t.id}`, opts); toast.success("Tercerizado enviado a la papelera"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "No se pudo borrar"); }
  };
  const exportar = (tipo) => exportarReporte(tipo, {
    titulo: "Tercerizados",
    filtros: q.trim() ? `Búsqueda: ${q.trim()}` : "",
    columnas: [{ l: "Razón social" }, { l: "RUC" }, { l: "Teléfono" }, { l: "Dirección" }, { l: "Contratos activos", num: true }, { l: "Obras", num: true },
      { l: "Monto contratado", num: true }, { l: "Certificado", num: true }, { l: "Pagado", num: true }, { l: "Saldo", num: true }],
    filas: filas.map(t => [t.razon_social, t.ruc, t.telefono, t.direccion, t.contratos_activos, t.obras, t.monto_contratado, t.certificado, t.pagado, t.saldo]),
    totales: ["Totales", "", "", "", "", "", suma("monto_contratado"), suma("certificado"), suma("pagado"), suma("saldo")],
    archivo: "Tercerizados",
  }, setExportando);

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="tesoreria-tercerizados">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Handshake size={20} className="text-[#10B981]" /> Tercerizados</h1>
          <p className="text-xs text-gray-500 mt-0.5">Registro de tercerizados: sus contratos en las obras, lo pagado y el historial. Se eligen desde los contratos de Obra y en Pagos Serv. Tercerizados.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportar("pdf")} disabled={!!exportando} className="flex items-center gap-1 text-xs font-semibold border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50" data-testid="terc-pdf"><Download size={13} /> PDF</button>
          <button onClick={() => exportar("excel")} disabled={!!exportando} className="flex items-center gap-1 text-xs font-semibold border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50" data-testid="terc-excel"><FileSpreadsheet size={13} /> Excel</button>
          <button onClick={() => exportar("ver")} disabled={!!exportando} className="flex items-center gap-1 text-xs font-semibold border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50" data-testid="terc-preview"><Eye size={13} /> Vista previa</button>
          <button onClick={() => setForm({})} className="flex items-center gap-1.5 bg-[#10B981] hover:bg-[#059669] text-white rounded-lg px-3 py-2 text-xs font-semibold" data-testid="terc-nuevo"><Plus size={14} /> Nuevo tercerizado</button>
        </div>
      </div>

      {(data?.sin_registrar || []).length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800" data-testid="terc-sin-registrar">
          <p className="font-semibold mb-1.5">Contratistas de contratos de obra que todavía no están en el registro:</p>
          <div className="flex flex-wrap gap-2">
            {data.sin_registrar.map(s => (
              <button key={s.nombre} onClick={() => setForm({ razon_social: s.nombre, ruc: s.ruc || "" })}
                className="flex items-center gap-1 bg-white border border-amber-300 rounded-full px-2.5 py-1 hover:bg-amber-100" data-testid={`terc-registrar-${s.nombre}`}>
                <UserPlus size={12} /> {s.nombre} <span className="text-amber-600">({s.contratos})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar razón social, RUC, teléfono o dirección…"
          className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-2 text-sm" data-testid="terc-buscar" />
      </div>

      <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-x-auto">
        {!data ? <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div> : (
          <table className="w-full text-sm" data-testid="terc-tabla">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase"><tr>
              <th className="text-left px-4 py-2.5">Razón social</th><th className="text-left px-4 py-2.5">RUC</th><th className="text-left px-4 py-2.5">Teléfono</th>
              <th className="text-left px-4 py-2.5">Dirección</th><th className="text-right px-4 py-2.5">Contratos activos</th><th className="text-right px-4 py-2.5">Obras</th>
              <th className="text-right px-4 py-2.5">Monto contratado</th><th className="text-right px-4 py-2.5">Pagado</th><th className="text-right px-4 py-2.5">Saldo</th><th className="px-4 py-2.5" />
            </tr></thead>
            <tbody>
              {filas.length === 0 && <tr><td colSpan={10} className="py-10 text-center text-gray-400">{(data.tercerizados || []).length ? "Sin coincidencias" : "Todavía no hay tercerizados registrados"}</td></tr>}
              {filas.map(t => (
                <tr key={t.id} className="border-t border-gray-50 hover:bg-emerald-50/30" data-testid={`terc-fila-${t.id}`}>
                  <td className="px-4 py-2.5 font-semibold text-gray-800">{t.razon_social}</td>
                  <td className="px-4 py-2.5 text-gray-600">{t.ruc || "—"}</td>
                  <td className="px-4 py-2.5 text-gray-600">{t.telefono || "—"}</td>
                  <td className="px-4 py-2.5 text-gray-600 max-w-[220px] truncate">{t.direccion || "—"}</td>
                  <td className="px-4 py-2.5 text-right">{t.contratos_activos}</td>
                  <td className="px-4 py-2.5 text-right">{t.obras}</td>
                  <td className="px-4 py-2.5 text-right">{formatPYG(t.monto_contratado)}</td>
                  <td className="px-4 py-2.5 text-right text-emerald-700">{formatPYG(t.pagado)}</td>
                  <td className="px-4 py-2.5 text-right font-bold">{formatPYG(t.saldo)}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-right">
                    <button onClick={() => setDetalle(t)} title="Ver contratos, obras e historial" className="p-1.5 rounded-lg hover:bg-gray-100 text-[#2E6DA4]" data-testid={`terc-ver-${t.id}`}><Eye size={15} /></button>
                    <button onClick={() => setForm(t)} title="Editar" className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" data-testid={`terc-editar-${t.id}`}><Pencil size={14} /></button>
                    <button onClick={() => borrar(t)} title="Enviar a la papelera" className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-300 hover:text-red-600" data-testid={`terc-borrar-${t.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
            {filas.length > 0 && (
              <tfoot className="bg-emerald-50"><tr className="font-bold text-gray-800" data-testid="terc-totales">
                <td colSpan={6} className="px-4 py-2.5">Totales ({filas.length})</td>
                <td className="px-4 py-2.5 text-right">{formatPYG(suma("monto_contratado"))}</td>
                <td className="px-4 py-2.5 text-right">{formatPYG(suma("pagado"))}</td>
                <td className="px-4 py-2.5 text-right">{formatPYG(suma("saldo"))}</td>
                <td />
              </tr></tfoot>
            )}
          </table>
        )}
      </div>

      {form && <FormTercerizado inicial={form} onClose={() => setForm(null)} onSaved={load} />}
      {detalle && <DetalleTercerizado tercerizado={detalle} onClose={() => setDetalle(null)} />}
    </div>
  );
}
